
import Multiselect from '@vueform/multiselect';
import { defineComponent } from 'vue';
import { Field, ErrorMessage } from 'vee-validate';

import { capitalize } from '@/utils/text';
import { maska } from 'maska';

/**
 * TODO:
 *
 * Recreate this component when the backend start creating auto-generated
 * passwords
 */

export default defineComponent({
  name: 't2o-form-field',
  components: {
    ErrorMessage,
    Field,
    Multiselect,
  },
  directives: { maska },
  emits: [
    'blur',
    'keyup',
    'input',
    'select',
    'change',
    'fileUpload',
    'selectChange',
    'selectCloseOrDeselect',
    'confirmButton',
    'colorPickerChange',
    'colorPickerActiveChange',
    'update:modelValue',
  ],
  props: {
    slot: {
      type: Object,
      default(html) {
        return html ? html : '';
      },
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    hideSelected: {
      type: Boolean,
      default: false,
    },
    accepts: {
      type: String,
      default: '.png, .jpg, .jpeg',
    },
    autocomplete: {
      default: 'off',
      type: String,
    },
    containerClass: {
      default: '',
      type: String,
    },
    formControlClass: {
      default: 'form-control',
      type: String,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    name: {
      required: true,
      type: String,
    },
    label: {
      default: '',
      type: String,
    },
    options: {
      default: () => [],
      type: Array,
    },
    optionLabel: {
      default: 'label',
      type: String,
    },
    optionValue: {
      default: 'value',
      type: String,
    },
    placeholder: {
      default: null,
      type: String,
    },
    required: {
      default: false,
      type: Boolean,
    },
    searchable: {
      default: true,
      type: Boolean,
    },
    selectValue: {
      default: null,
      type: String,
    },
    showLabel: {
      default: true,
      type: Boolean,
    },
    type: {
      default: 'text',
      type: String,
    },
    useBoolValue: {
      default: false,
      type: Boolean,
    },
    mask: {
      default: null,
      type: String,
    },
    currency: {
      default: 'dollar',
      type: String,
    },
    dateFormat: {
      default: 'MM/DD/YYYY',
      type: String,
    },
    dateValueFormat: {
      default: 'YYYY-MM-DD',
      type: String,
    },
    disabledDates: {
      type: Function,
      default: () => undefined,
    },
    buttonTitle: {
      default: null,
      type: String,
    },
    modelValue: { default: null },
  },
  data: () => ({
    // eslint-disable-next-line
    selectValueModel: undefined as any,
    showPassword: undefined as any,
  }),
  mounted() {
    if (this.selectValue) {
      this.setSelectValue(this.selectValue);
    }
  },

  computed: {
    fieldValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    checkboxFieldType() {
      return this.type === 'checkbox';
    },
    colClass() {
      return `mb-10 ${this.containerClass}`;
    },
    fieldLabel() {
      if (this.label && this.label.trim().length > 0) return this.label;

      return this.name
        .split(/_/g)
        .map((word) => capitalize(word))
        .join(' ');
    },
    fieldPlaceholder() {
      return this.placeholder || this.fieldLabel;
    },
    fileFieldType() {
      return this.type === 'file';
    },
    labelClass() {
      const required = this.required ? 'required' : '';
      return `${required} form-label fs-6 fw-bolder text-dark`;
    },
    multiselectFieldType() {
      return this.type === 'multiselect';
    },
    multipleSelectionTag() {
      return this.type == 'multi-tag';
    },
    numberFieldType() {
      return this.type === 'number';
    },
    radioButtonFieldType() {
      return this.type === 'radio';
    },
    selectFieldPlaceholder() {
      return this.placeholder || `Select ${this.label}`;
    },
    selectFieldType() {
      return this.type === 'select';
    },
    textareaFieldType() {
      return this.type === 'textarea';
    },
    maskFieldType() {
      return this.type === 'mask';
    },
    currencyFieldType() {
      return this.type === 'currency';
    },
    passwordFieldType() {
      return this.type === 'password';
    },
    datePickerType() {
      return this.type === 'datepicker';
    },
    inputGroupType() {
      return this.type === 'inputgroup';
    },
    dateRangeType() {
      return this.type === 'daterange';
    },
    colorPickerFieldType() {
      return this.type === 'colorpicker';
    },
  },
  methods: {
    checkForDecimalPlaces(ev) {
      const value = ev.target.value;

      const decimals: string = value.toString().split('.')[1];

      if (!decimals) return;

      if (decimals.length > 2) {
        ev.preventDefault();
        ev.target.value = parseFloat(value).toFixed(
          decimals.charAt(1) === '0' ? 1 : 2
        );
      }
    },
    handleFileUpload() {
      const file = (
        document.querySelector(`input[name="${this.name}"]`) as unknown as {
          files: Blob[];
        }
      ).files[0];

      if (!file) this.$emit('fileUpload', null);

      var reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        this.$emit('fileUpload', reader.result);
      };
    },
    handleSelectChange(value) {
      this.$emit('selectChange', value);
    },
    handleSelectCloseOrClose() {
      this.$emit('selectCloseOrDeselect');
    },
    handleColorPickerChange(value) {
      this.$emit('colorPickerChange', value);
    },
    handleColorPickerActiveChange(value) {
      this.$emit('colorPickerActiveChange', value);
    },
    setShowPassword() {
      this.showPassword = !this.showPassword;
    },
    setSelectValue(selectValue) {
      if (!selectValue) {
        // eslint-disable-next-line
        (this.$refs.multiselect as any).select({});
      }

      if (selectValue && this.options.length > 0) {
        // eslint-disable-next-line
        const foundSelectOption = this.options.find((option: any) => {
          return option[this.optionValue] === selectValue;
        });

        if (!foundSelectOption) {
          // eslint-disable-next-line
          (this.$refs.multiselect as any).select({});
          return;
        }
        // eslint-disable-next-line
        (this.$refs.multiselect as any).select(foundSelectOption);
      }
    },
    clearSelected() {
      this.setSelectValue('');

      return;
    },
    handleKeyup(e) {
      this.$emit('keyup', e);
    },
    handleBlur(e) {
      this.$emit('blur', e);
    },
    handleInput(e) {
      this.$emit('input', e);
    },
    handleSelect(e) {
      this.$emit('select', e);
    },
    handleChange(e) {
      this.$emit('change', e);
    },
    handleConfirm() {
      this.$emit('confirmButton', true);
    },
  },
  watch: {
    options(newValue, oldValue) {
      if (newValue.length === oldValue.length) return;

      this.setSelectValue(this.selectValue);
    },
    selectValue(newValue, oldValue) {
      if (newValue === oldValue) return;

      this.setSelectValue(newValue);
    },
  },
});
